'use client';

import { useEffect } from 'react';
import {
  TIKTOK_MAX_WIDTH,
  TIKTOK_MIN_WIDTH,
  isSafeEmbedUrl,
  resolveTikTokId,
} from '../providers';
import { ensureTikTokEmbedsProcessed } from '../tiktokEmbedScript';

interface TikTokEmbedProps {
  url?: string;
  src?: string;
  id?: string;
  title?: string;
}

/**
 * Renders TikTok via the official `embed.js`, which replaces the `.tiktok-embed`
 * blockquote with a correctly-sized player card. TikTok's `embed/v2` iframe
 * doesn't broadcast its content height, and the card's height (video region +
 * caption/music chrome) can't be expressed as a fixed CSS aspect ratio, so a
 * bare iframe always leaves whitespace or clips; letting embed.js own the
 * sizing is the only reliable way to match the native card.
 *
 * Script loading/reprocessing (a single shared tag, re-triggered for unprocessed
 * blockquotes after client navigation) is handled by `ensureTikTokEmbedsProcessed`.
 * The canonical id is reconstructed and used as `data-video-id` (what embed.js
 * builds the player from); the `cite` link is only set when it's a safe http(s)
 * URL.
 */
export function TikTokEmbed({ url, src, id, title }: TikTokEmbedProps) {
  const videoId = resolveTikTokId(id ?? url ?? src);
  const cite = url ?? src;

  useEffect(() => {
    if (!videoId) return;
    ensureTikTokEmbedsProcessed();
  }, [videoId]);

  if (!videoId) return null;

  return (
    <div className='my-lg flex justify-center' aria-label={title || undefined}>
      <blockquote
        className='tiktok-embed'
        cite={isSafeEmbedUrl(cite) ? cite : undefined}
        data-video-id={videoId}
        style={{ maxWidth: TIKTOK_MAX_WIDTH, minWidth: TIKTOK_MIN_WIDTH }}
      >
        <section />
      </blockquote>
    </div>
  );
}
